import React from 'react'
import { Image } from '@/lib/next-shim'
import { useTranslations } from '@/lib/next-shim'

import footerLogo from '@/assets/svg/footer-logo.svg'
import Container from '@/components/ui/layouts/Container'
import { getEnv } from '@/lib/env'
import { Link } from '@/lib/i18n/settings'
import { shouldUseCustomBranding } from '@/utils/landing-variant'

import { NavLinks, NavLinksProps } from './NavLinks'

type FooterProps = {
  className?: string
  navMenu: NavLinksProps['navItems']
}

export function Footer(props: FooterProps) {
  const { className, navMenu } = props
  const t = useTranslations()

  // Get branding information for white labeling
  const isCustomBranded = shouldUseCustomBranding()
  const brandLogo = getEnv('NEXT_PUBLIC_BRAND_LOGO')
  const brandName = getEnv('NEXT_PUBLIC_BRAND_NAME', 'Medconnect Platform')

  const navigation = {
    innovations: [
      { label: t('marketing.layout.Footer.innovationsCategory.item.cart'), name: 'Chariot', href: '#' },
      { label: t('marketing.layout.Footer.innovationsCategory.item.briefcase'), name: 'Teleconsultation briefcase', href: '#' },
      { label: t('marketing.layout.Footer.innovationsCategory.item.medicalInformatic'), name: 'Medical informatic', href: '#' },
    ],
    products: [
      { label: t('marketing.layout.Footer.productsCategory.item.software'), name: 'Software', href: '#' },
      { label: t('marketing.layout.Footer.productsCategory.item.camera'), name: 'Camera', href: '#' },
      { label: t('marketing.layout.Footer.productsCategory.item.ultrasound'), name: 'Ultrasound', href: '#' },
    ],
    company: [
      { label: t('marketing.layout.Footer.companyCategory.item.about'), name: 'About', href: '#' },
      { label: t('marketing.layout.Footer.companyCategory.item.faqs'), name: 'FAQs', href: '/#faqs' },
      { label: t('marketing.layout.Footer.companyCategory.item.help'), name: 'Help', href: '/help' },
      { label: t('marketing.layout.Footer.companyCategory.item.contact'), name: 'Contact', href: '#' },
    ],
    legal: [
      { label: t('marketing.layout.Footer.legalCategory.item.claim'), name: 'Claim', href: '#' },
      { label: t('marketing.layout.Footer.legalCategory.item.privacy'), name: 'Privacy', href: '#' },
      { label: t('marketing.layout.Footer.legalCategory.item.terms'), name: 'Terms', href: '#' },
    ],
    social: [
      {
        name: 'Facebook',
        href: '#',
        icon: (props: React.SVGProps<SVGSVGElement>) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path
              fillRule="evenodd"
              d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
              clipRule="evenodd"
            />
          </svg>
        ),
      },
      {
        name: 'Twitter',
        href: '#',
        icon: (props: React.SVGProps<SVGSVGElement>) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
          </svg>
        ),
      },
    ],
  }

  // Render simplified footer for custom branded sites
  if (isCustomBranded) {
    return (
      <footer className={className}>
        <Container>
          <div className="flex flex-col items-center justify-center space-y-6 px-6 py-16 lg:px-8">
            {brandLogo ? (
              <Image alt={brandName} src={brandLogo} width={178} height={100} className="h-24 w-auto" />
            ) : (
              <Image alt="footer-logo" src={footerLogo} width={178} height={100} className="h-24" />
            )}
          </div>
        </Container>
        <Container>
          <div className="border-t border-gray-900/10 py-6">
            <div className="flex items-center justify-center">
              <p className="text-center text-xs leading-5 text-gray-500">
                {t('marketing.layout.Footer.copyrightBrand', { year: new Date().getFullYear(), brand: brandName })}
              </p>
            </div>
          </div>
        </Container>
      </footer>
    )
  }

  return (
    <footer className={className}>
      <Container>
        <div className="flex flex-col items-center justify-center space-y-6 px-6 py-16 lg:px-8">
          <Image alt="footer-logo" src={footerLogo} width={178} height={100} className="h-24" />
          <nav className="-mb-6 columns-2 flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:justify-center sm:space-x-12" aria-label="Footer">
            <NavLinks navItems={navMenu} />
          </nav>
        </div>
        {/* <div className="lg:grid lg:grid-cols-5 lg:gap-10 pt-16 pb-6">
          <div className="max-w-md m-auto text-center lg:text-left lg:col-span-2 space-y-8">
            <Image alt="footer-logo" src={footerLogo} width={178} height={100} className="h-24" />
            <p className="text-sm leading-6 text-gray-600">{t('footer.companyMission')}</p>
          </div>
          <div className="lg:col-span-3 grid grid-cols-3 gap-5 mt-16 lg:mt-0">
            <div className="sm:pl-4">
              <div className="flex flex-col items-start justify-center">
                <h3 className="text-base font-medium text-primary">{t('footer.companyCategory.title')}</h3>
                <ul role="list" className="mt-6 space-y-4">
                  {navigation.company.map((item) => (
                    <li key={item.name}>
                      <a href={item.href} className="text-sm text-gray-600 hover:text-primary">
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="sm:pl-4">
              <div className="flex flex-col items-start justify-center">
                <h3 className="text-base font-medium text-primary">{t('footer.productsCategory.title')}</h3>
                <ul role="list" className="mt-6 space-y-4">
                  {navigation.products.map((item) => (
                    <li key={item.name}>
                      <a href={item.href} className="text-sm text-gray-600 hover:text-primary">
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="sm:pl-4">
              <div className="flex flex-col items-start justify-center">
                <h3 className="text-base font-medium text-primary">{t('footer.innovationsCategory.title')}</h3>
                <ul role="list" className="mt-6 space-y-4">
                  {navigation.innovations.map((item) => (
                    <li key={item.name}>
                      <a href={item.href} className="text-sm text-gray-600 hover:text-primary">
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div> */}
      </Container>
      <Container>
        <div className="border-t border-gray-900/10 py-6 md:flex md:items-center md:justify-between">
          <div className="flex justify-center space-x-6 md:order-2">
            {navigation.social.map((item) => (
              <a key={item.name} href={item.href} className="text-gray-600 hover:text-gray-700">
                <span className="sr-only">{item.name}</span>
                <item.icon className="h-6 w-6" aria-hidden="true" />
              </a>
            ))}
          </div>
          <div className="flex items-center justify-center space-x-2 mt-6 md:order-1 md:mt-0">
            <Link href="/sitemap" className="underline decoration-solid text-xs text-gray-600 hover:text-primary">
              {t('marketing.layout.Footer.sitemap')}
            </Link>
            <Link href="/accessibility" className="underline decoration-solid text-xs text-gray-600 hover:text-primary">
              {t('marketing.layout.Footer.accessibility')}
            </Link>
            <Link href="/help" className="underline decoration-solid text-xs text-gray-600 hover:text-primary">
              {t('marketing.layout.Footer.help')}
            </Link>
            <p className="text-center text-xs leading-5 text-gray-500">&copy; {t('marketing.layout.Footer.copyrights')}</p>
            <div className="hidden md:block">
              <div className="h-7 flex space-x-1">
                {navigation.legal.map((item) => (
                  <div key={item.name}>
                    <a href={item.href} className="underline decoration-solid text-xs text-gray-600 hover:text-primary">
                      {item.label}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  )
}
